import * as Sentry from '@sentry/react';
import { LOG_MODULES } from './modules';
import { trackException } from './exceptionTracker';
import { resolveListgenUserID } from './resolveListgenUserID';
import { getAuthStorageDiagnostics } from './storageDiagnostics';

// Known logout modes (see handleLogoutFn callers).
const LOGOUT_MODE_REASONS = {
  0: 'silent_storage_reset',
  1: 'user_or_session_dialog_logout',
  6: 'forced_session_expired',
};

// Modes where the user did not choose to log out — these are the
// "forcefully asking for logout" complaints, logged as warnings.
const FORCED_MODES = new Set([6]);

/**
 * Log a logout to Sentry (breadcrumb + event).
 * MUST be called BEFORE storages are reset, so the ListGen user ID can
 * still be resolved from storage.
 * @param {number} logoutMode
 * @param {boolean} reloadPage
 */
export const logLogout = (logoutMode, reloadPage) => {
  try {
    const listgenUserID = resolveListgenUserID();
    const reason = LOGOUT_MODE_REASONS[logoutMode] || 'unknown';
    const isForced = FORCED_MODES.has(logoutMode);
    const pathname =
      (typeof window !== 'undefined' && window.location?.pathname) || '';

    Sentry.addBreadcrumb({
      category: 'auth',
      message: `Logout (mode=${logoutMode}, ${reason})`,
      level: isForced ? 'warning' : 'info',
      data: { logoutMode, reason, pathname, reloadPage },
    });

    const storageDiagnostics = getAuthStorageDiagnostics();

    // Mode 0 is a routine pre-auth storage reset (e.g. visiting /login):
    // breadcrumb only — UNLESS we're about to wipe a partial or valid session.
    // A partial session (token present but login invalid) means the user just
    // got bounced back to sign-in and we're destroying what's left of their
    // session — exactly the "knocked back to login" complaint.
    if (logoutMode === 0) {
      const hasSessionRemnants =
        storageDiagnostics.hasOAuthToken ||
        storageDiagnostics.stateIsUserLoggedIn;
      if (!hasSessionRemnants) {
        return;
      }
      trackException({
        module: LOG_MODULES.LOGOUT,
        code: storageDiagnostics.isValidLogin
          ? 'VALID_SESSION_WIPED'
          : 'PARTIAL_SESSION_WIPED',
        error: new Error(
          storageDiagnostics.isValidLogin
            ? 'Sign-in screen wiped a fully valid session'
            : 'Sign-in screen wiped a partial session (user was bounced back to login)'
        ),
        level: 'warning',
        listgenUserID: listgenUserID || undefined,
        data: { logoutMode, reason, pathname, reloadPage, storageDiagnostics },
      });
      return;
    }

    trackException({
      module: LOG_MODULES.LOGOUT,
      code: `LOGOUT_MODE_${logoutMode}`,
      error: new Error(`Logout triggered (mode=${logoutMode}, ${reason})`),
      level: isForced ? 'warning' : 'info',
      listgenUserID: listgenUserID || undefined,
      data: {
        logoutMode,
        reason,
        isForced,
        pathname,
        reloadPage,
        storageDiagnostics,
      },
    });
  } catch {
    // Logging must never block the logout flow
  }
};
